<template>
  <header>
    <section class="xl:container xl:mx-auto xl:px-20 2xl:px-64">
      <div class="bg-white rounded-b-2xl shadow-lg pt-14 lg:pt-16 xl:pt-6 pb-4 leading-tight">
        <!-- Titles and search input -->
        <div class="flex gap-[10px] items-center lg:block px-4 sm:px-10 mb-1 lg:mb-4">
          <h1 class="text-app-primary-1-red--fire-brick text-2xl md:text-4xl lg:text-5xl font-semibold mb-1">{{ title }}</h1>
          <div class="lg:grid grid-cols-12 gap-x-8">
            <p v-if="introductionMobile" class="lg:hidden text-xs">{{ introductionMobile }}</p>
            <p class="lg:col-span-6 text-sm lg:text-lg xl:text-xl" :class="{ 'hidden lg:block': introductionMobile }">{{ introduction }}</p>
            <!-- desktop search input -->
            <div class="hidden lg:block lg:col-span-4 lg:col-end-13 relative">
              <input
                :value="keyword"
                type="text"
                :placeholder="t_('keyword-placeholder')"
                class="c-form-input c-search-placeholder w-full"
                @input="(event) => emit('setKeyword', (event.target as HTMLInputElement).value)"
                @focus="onSearchFocus"
                @blur="onSearchBlur"
              />
              <AtomIconSearch class="c-search-icon" />
            </div>
          </div>
        </div>

        <!-- mobile search input + filters -->
        <div class="px-4 sm:px-10">
          <!-- mobile search input -->
          <div class="w-full max-w-[180px] h-7 mb-2 lg:hidden absolute top-0 mt-3 right-0 mr-12">
            <input
              :value="keyword"
              type="text"
              :placeholder="t_('keyword-placeholder')"
              class="c-search-placeholder w-full border border-black border-solid bg-white md:text-xs h-full py-2 px-3 rounded-full"
              @input="(event) => $emit('setKeyword', (event.target as HTMLInputElement).value)"
              @focus="onSearchFocus"
              @blur="onSearchBlur"
            />
            <AtomIconSearch class="c-search-icon" />
          </div>

          <!-- start filters -->
          <ul class="flex flex-wrap">
            <li v-if="showDatePicker" class="s-datepicker mr-4 relative" style="z-index: 30">
              <MoleculeDatepickerLegacy
                background-color="bg-app-primary-grey--black-squeeze"
                text-color="black hover:text-app-primary-1-red--torch"
                size="medium"
                :enable-icon-left="false"
                :shadowed="false"
                :icon-size="3"
                class-list="hidden lg:flex lg:col-span-3 xl:col-span-2 px-6 py-2"
              />
            </li>
            <li v-for="({ values, key }, index) in facets" :key="key" class="relative z-10 mr-4">
              <MoleculeButton
                background-color="bg-app-primary-grey--black-squeeze"
                text-color="black hover:text-app-primary-1-red--torch"
                size="medium"
                :shadowed="false"
                :enable-icon-left="false"
                class="hidden lg:flex ignore-close"
                @click="toggleOpen(key)"
              >
                {{ t_(key) }}
                <div>
                  <AtomIcon
                    icon="chevron-down"
                    classes="text-app-primary-1-red--fire-brick w-3 h-3 ml-2 transform transition duration-200 shrink-0"
                    :class="{ 'rotate-180': key === currentlyOpen }"
                  />
                </div>
              </MoleculeButton>

              <div v-show="key === currentlyOpen" v-closable="{ executable: closeAll, exclude: '.ignore-close' }" class="c-filter-dropdown hidden lg:block ignore-close">
                <div class="c-filter-dropdown__content flex flex-col p-6 z-50 bg-white rounded-br-3xl rounded-bl-3xl" :style="{ 'margin-left': `${index * -87}%` }">
                  <div class="flex-auto overflow-auto">
                    <template v-if="values.length <= 8">
                      <ul class="flex flex-row flex-wrap w-full">
                        <li v-for="value in values" :key="value.value" class="mr-4 mb-4">
                          <MoleculeButtonSearchFilter :state="getState(value.active, value.count)" @click="emit('toggleFacet', { name: key, value: value.value })">
                            {{ value.label || t_(value.value) }}
                          </MoleculeButtonSearchFilter>
                        </li>
                      </ul>
                    </template>
                    <template v-else>
                      <ul class="grid lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                        <li v-for="value in sortBy(values, (value) => value.label || $t(`facet.${value.value}`))" :key="value.value">
                          <AtomLink
                            class="mr-4 mb-2 cursor-pointer hover:text-app-primary-1-red--fire-brick underline font-medium text-sm"
                            :class="{
                              'text-app-primary-1-red--fire-brick font-semibold': value.active,
                              'text-app-primary-grey--concrete pointer-events-none': value.count === 0,
                            }"
                            :fallback-component="value.count !== 0 ? 'button' : 'span'"
                            @click="emit('toggleFacet', { name: key, value: value.value })"
                          >
                            {{ value.label || $t(`facet.${value.value}`) }}
                          </AtomLink>
                        </li>
                      </ul>
                    </template>
                  </div>

                  <div class="flex items-center pt-5 mt-2 border-t-2 border-solid border-app-primary-grey--black-squeeze">
                    <span
                      v-if="dateIsSpecified || hasKeywordSelection || hasActiveRegularFacetSelection"
                      class="underline mr-3 font-semibold text-sm cursor-pointer"
                      @click="emit('reset')"
                    >
                      {{ $t('overview-search-reset') }}
                    </span>
                    <div class="ml-auto font-semibold text-sm cursor-pointer shrink-0 ignore-close" @click="toggleOpen(key)">
                      <MoleculeButton :enable-icon-left="false" background-color="bg-app-primary-1-orange--bittersweet group-hover:bg-app-primary-1-red--fire-brick" size="medium">
                        {{ $t('overview-search-show-results', totalHits) }}
                      </MoleculeButton>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>

          <!-- start mobile filters -->
          <div class="flex flex-col sm:flex-row gap-2 lg:hidden">
            <MoleculeButtonSearchFilter class="h-[39px] pl-5 flex flex-auto items-center justify-between" state="neutral" @click="toggleMobileDatePicker">
              {{ $t('overview-search-pick-date') }}
              <AtomIconCalendar class="w-4 h-4 ml-1 text-app-primary-1-red--fire-brick shrink-0" />
            </MoleculeButtonSearchFilter>

            <MoleculeButtonSearchFilter class="h-[39px] pl-5 flex flex-auto items-center justify-between" state="neutral" @click="toggleMobileFilter">
              {{ $t('overview-search-toggle-filters') }}
              <AtomIconAppFilter class="w-4 h-4 ml-1 text-app-primary-1-red--fire-brick shrink-0" />
            </MoleculeButtonSearchFilter>
          </div>
          <!-- end mobile filters -->
        </div>
      </div>
    </section>

    <section class="xl:container xl:mx-auto px-8 sm:px-10 xl:px-20 2xl:px-64 flex mt-5">
      <!-- Display active filters if any -->
      <!-- BEWARE: the HTML of the active filters is not very semantic because all active filters need to be displayed inline so they can be wrapped after one another -->
      <div v-if="dateIsSpecified || hasKeywordSelection || hasActiveTagsFacetSelection || hasActiveRegularFacetSelection" class="text-sm leading-none flex items-center flex-wrap">
        <!-- Active filter with date -->
        <template v-if="dateIsSpecified">
          <p class="font-medium mr-1 mb-1 shrink-0">{{ $t('filters.concerts-from-date', totalHits) }}</p>
          <MoleculeFacet
            class="mb-1 mr-2"
            :title="d_(date, 'short-date')"
            @click="
              () => {
                removeDateFilter(d_(date, 'short-date'))
                eventOverviewStore.setDate()
              }
            "
          >
            <AtomIconX class="w-2 h-2 cursor-pointer shrink-0" />
          </MoleculeFacet>
        </template>

        <!-- Active filters with tag -->
        <template v-if="hasActiveTagsFacetSelection">
          <p class="font-medium mr-1 mb-1 shrink-0">{{ $t('filters.active-tags') }}</p>
          <div v-for="{ facet, label, value } in activeTagFacets" :key="value" class="mr-2 mb-1">
            <MoleculeFacet :title="label || t_(value)" @click="emit('toggleFacet', { name: facet, value })">
              <AtomIconX class="w-2 h-2 cursor-pointer shrink-0" />
            </MoleculeFacet>
          </div>
        </template>

        <!-- Active filters with search words -->
        <template v-if="hasKeywordSelection">
          <p class="font-medium mr-1 mb-1 shrink-0">{{ $t('filters.active-search-keywords') }}</p>
          <div class="mr-2 mb-1">
            <MoleculeFacet :title="keyword" @click="emit('setKeyword', '')">
              <AtomIconX class="w-2 h-2 cursor-pointer shrink-0" />
            </MoleculeFacet>
          </div>
        </template>

        <!-- Active filters with facet values -->
        <template v-if="hasActiveRegularFacetSelection">
          <p class="font-medium mr-1 mb-1 shrink-0">{{ $t('filters.active-filters') }}</p>
          <div v-for="{ facet, label, value } in activeRegularFacets" :key="value" class="mr-2 mb-1">
            <MoleculeFacet
              :title="label || t_(value)"
              @click="
                () => {
                  emit('toggleFacet', { name: facet, value })
                  removeFilter()
                }
              "
            >
              <AtomIconX class="w-2 h-2 cursor-pointer shrink-0" />
            </MoleculeFacet>
          </div>
        </template>

        <!-- Reset filter link -->
        <p v-if="dateIsSpecified || hasKeywordSelection || hasActiveRegularFacetSelection" class="underline cursor-pointer font-medium mb-1" @click="emit('reset')">
          {{ $t('filters.remove-filters') }}
        </p>
      </div>
    </section>

    <section v-if="mobileFilters" class="fixed bg-app-primary-grey--black-squeeze top-0 right-0 bottom-0 left-0 z-50 lg:hidden overflow-auto pb-20">
      <div class="px-4 pt-10">
        <h5 class="flex items-center">
          <AtomIconAppFilter class="w-6 h-6 text-app-primary-1-red--torch mx-3 shrink-0" />
          <span class="text-app-primary-1-red--fire-brick text-2xl font-semibold">
            {{ $t('overview-search-toggle-filters') }}
          </span>
        </h5>

        <hr class="border-t border-app-primary-1-red--fire-brick mt-4" />

        <ul>
          <li v-for="{ values, key } in facets" :key="key" class="relative border-b border-solid border-app-primary-1-red--fire-brick">
            <span class="flex items-center text-xl text-app-primary-1-red--fire-brick py-6 font-semibold cursor-pointer ignore-close" @click="toggleOpen(key)">
              {{ t_(key) }}
              <AtomIcon
                icon="chevron-down"
                classes="text-app-primary-1-red--fire-brick w-4 h-4 ml-2 transform transition duration-200 ml-auto shrink-0"
                :class="{ 'rotate-180': key === currentlyOpen }"
              />
            </span>

            <template v-if="values.length <= 25">
              <ul v-show="key === currentlyOpen" class="mb-6 ignore-close">
                <li v-for="value in values" :key="value.value" class="mr-4 mb-4 inline-block">
                  <MoleculeButtonSearchFilter
                    mobile
                    class="ignore-close"
                    :state="getState(value.active, value.count)"
                    :class="{
                      'text-app-primary-1-red--fire-brick': !value.active && value.count !== 0,
                      'text-white font-semibold': value.active,
                      'text-app-primary-grey--concrete pointer-events-none': value.count === 0,
                    }"
                    @click="emit('toggleFacet', { name: key, value: value.value })"
                  >
                    {{ value.label || t_(value.value) }}
                  </MoleculeButtonSearchFilter>
                </li>
              </ul>
            </template>
            <template v-else>
              <div v-show="key === currentlyOpen" class="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-2 ignore-close">
                <div
                  v-for="value in sortBy(values, (value) => value.value)"
                  :key="value.value"
                  class="mr-4 mb-1 cursor-pointer hover:underline text-sm ignore-close"
                  :class="{
                    'text-app-primary-1-red--fire-brick font-semibold': value.active,
                    'text-app-primary-grey--concrete pointer-events-none': value.count === 0,
                  }"
                  @click="emit('toggleFacet', { name: key, value: value.value })"
                >
                  {{ value.label || t_(value.value) }}
                </div>
              </div>
            </template>
          </li>
        </ul>
      </div>

      <div class="bg-white py-3 rounded-t-3xl z-10 fixed bottom-0 w-full px-5 sm:px-6 shadow-2xl">
        <ol class="flex items-center">
          <li
            v-if="dateIsSpecified || hasKeywordSelection || hasActiveRegularFacetSelection"
            class="flex flex-col items-center hover:text-app-primary-1-red--torch transition duration-300 ease-in-out mr-auto underline font-semibold leading-none cursor-pointer text-sm"
            @click="
              () => {
                emit('reset')
                toggleMobileFilter()
              }
            "
          >
            {{ $t('overview-search-reset') }}
          </li>
          <li class="flex flex-col items-center hover:text-app-primary-1-red--torch transition duration-300 ease-in-out ml-auto">
            <MoleculeButtonSearchFilter state="results" @click="toggleMobileFilter">
              {{ $t('overview-search-show-results', totalHits) }}
            </MoleculeButtonSearchFilter>
          </li>
        </ol>
      </div>

      <div class="bg-white top-0 right-0 p-5 absolute rounded-bl-3xl cursor-pointer" @click="toggleMobileFilter">
        <AtomIconX class="w-3 h-3 cursor-pointer shrink-0" />
      </div>
    </section>

    <section v-if="mobileDatePicker" class="fixed bg-app-primary-grey--black-squeeze top-0 right-0 bottom-0 left-0 z-50 lg:hidden">
      <div class="px-4 pt-10">
        <h5 class="flex items-center">
          <AtomIconAppFilter class="w-6 h-6 mx-3 text-app-primary-1-red--torch shrink-0" />
          <span class="text-app-primary-1-red--fire-brick text-2xl">
            {{ $t('overview-search-pick-date') }}
          </span>
        </h5>

        <hr class="border-t border-app-primary-1-red--fire-brick mt-4 mb-6" />

        <MoleculeDatepickerLegacy
          background-color="bg-app-primary-grey--black-squeeze"
          text-color="black hover:text-app-primary-1-red--torch"
          size="medium"
          :enable-icon-left="false"
          :shadowed="false"
          :icon-size="4"
          class-list="w-full"
          :show-button="false"
          :is-full-width="true"
          @set-date="toggleMobileDatePicker"
        />

        <div class="mt-6 mb-1 font-semibold text-sm">
          <div class="rounded-full h-3 w-3 bg-app-secondary-8-blue--medium-aquamarine inline-flex items-center justify-center mr-2"></div>
          {{ $t('overview-search-days-with-available-concerts') }}
        </div>
        <hr class="border-t border-app-primary-1-red--fire-brick mt-4 mb-6" />
      </div>
      <div class="bg-white top-0 right-0 p-5 absolute rounded-bl-3xl cursor-pointer" @click="toggleMobileDatePicker">
        <AtomIconX class="w-3 h-3 cursor-pointer shrink-0" />
      </div>
    </section>
  </header>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'
import sortBy from 'lodash/sortBy'

const props = defineProps({
  activeFacets: { type: Array as PropType<any[]>, required: false, default: () => [] },
  facets: { type: Array as PropType<any[]>, required: false, default: () => [] },
  introductionMobile: { type: String, required: true },
  introduction: { type: String, required: true },
  keyword: { type: String, required: false, default: '' },
  showDatePicker: { type: Boolean, required: false, default: false },
  title: { type: String, required: true },
  totalHits: { type: Number, required: true },
  translationGroup: { type: String, required: true },
})

const emit = defineEmits(['setKeyword', 'toggleFacet', 'reset'])

const eventOverviewStore = useEventOverviewStore()
const mobileMenusStore = useMobileMenusStore()
const { t_, d_ } = setupTranslationPrefixer('OverviewHeaderAndFilters', props.translationGroup)
const registerGtmFilterStart = setupGtmFilterStart()
const registerGtmDateFilterStart = setupGtmDateFilterStart()
const registerGtmFilterMenuClose = setupGtmFilterMenuClose()
const registerGtmFilterRemove = setupGtmFilterRemove()
const registerGtmDateFilterRemove = setupGtmDateFilterRemove()
const registerGtmSearchFocusOpen = setupGtmSearchFocusOpen()
const registerGtmSearchFocusClose = setupGtmSearchFocusClose()

const date = computed(() => eventOverviewStore.date)
const dateIsSpecified = computed(() => eventOverviewStore.dateIsSpecified)
const currentSearchTrackingSignature = computed(() => eventOverviewStore.searchTrackingSignature)
const lastCompletedSearchTrackingSignature = computed(() => eventOverviewStore.lastCompletedSearchTrackingSignature)
const searchTotalHits = computed(() => eventOverviewStore.totalHits)
const { cancelPendingSearchPerformed, queueSearchPerformed } = setupGtmSettledSearchPerformed(lastCompletedSearchTrackingSignature, searchTotalHits)

// Fire search_performed ~750 ms after the user stops typing (Control variant: debounce on keyword change).
const searchPerformedDebounced = debounce(() => {
  queueSearchPerformed(currentSearchTrackingSignature.value)
}, 750)

watch(
  () => props.keyword,
  (newKeyword) => {
    if (newKeyword) {
      searchPerformedDebounced()
    } else {
      searchPerformedDebounced.cancel()
      cancelPendingSearchPerformed()
    }
  },
)

onBeforeUnmount(() => {
  searchPerformedDebounced.cancel()
  cancelPendingSearchPerformed()
})

// When navigating from the homepage search, fire search_performed once the search settles.
onMounted(() => {
  if (eventOverviewStore.pendingSearchPerformed) {
    eventOverviewStore.setPendingSearchPerformed(false)
    queueSearchPerformed(currentSearchTrackingSignature.value)
  }
})

const onSearchFocus = () => {
  registerGtmSearchFocusOpen()
}

const onSearchBlur = () => {
  registerGtmSearchFocusClose()
}

const currentlyOpen = ref()
const closeAll = () => {
  currentlyOpen.value = undefined
}
const toggleOpen = (facet?: string) => {
  currentlyOpen.value = facet === currentlyOpen.value ? undefined : facet
}

const getState = (active: boolean, count: number) => {
  if (active) {
    return 'active'
  }

  if (count === 0) {
    return 'unavailable'
  }
}
const mobileFilters = ref(false)
const mobileDatePicker = ref(false)
const toggleMobileFilter = () => {
  mobileMenusStore.toggleMobileFilter()
  mobileFilters.value = mobileMenusStore.mobileFilterStatus

  if (mobileMenusStore.mobileFilterStatus === true) {
    registerGtmFilterStart()
  } else {
    registerGtmFilterMenuClose()
  }
}
const toggleMobileDatePicker = () => {
  mobileMenusStore.toggleMobileFilter()
  mobileDatePicker.value = mobileMenusStore.mobileFilterStatus
  if (mobileDatePicker.value === true) {
    registerGtmDateFilterStart()
  }
}

const removeFilter = () => {
  registerGtmFilterRemove()
}

const removeDateFilter = (formattedDate: string) => {
  registerGtmDateFilterRemove(formattedDate)
}

// 'tag' facets are displayed differently, hence we separate them from the other `activeFacets`
const activeTagFacets = computed(() => props.activeFacets.filter(({ facet }) => facet === 'tags'))
const activeRegularFacets = computed(() => props.activeFacets.filter(({ facet }) => facet !== 'tags'))

// several flags that determine if we need to show certain blocks
const hasActiveTagsFacetSelection = computed(() => activeTagFacets.value.length > 0)
const hasKeywordSelection = computed(() => props.keyword !== '')
const hasActiveRegularFacetSelection = computed(() => activeRegularFacets.value.length > 0)
</script>

<style lang="scss" scoped>
$height-dropdown-clamp: 8px;

@mixin dropdown-clamp() {
  &:after {
    @apply absolute bg-white;
    content: '';
    top: -4px;
    left: 60px;
    transform: translate(-50%, -50%);
    height: $height-dropdown-clamp;
    width: 17px;
    border-radius: 150px 150px 0 0;
  }
}

.c-filter-dropdown {
  @apply absolute w-full;
  @include dropdown-clamp;
  top: calc(#{$height-dropdown-clamp} + 38px);

  &::after {
    left: 50%;
  }
}

.c-filter-dropdown__content {
  $width-filter-dropdown: 640px;
  $width-filter-dropdown-xl: 768px;
  $width-filter-dropdown-2xl: 900px;
  top: calc(48px + #{$height-dropdown-clamp});
  box-shadow: 0px -8px 20px rgba(0, 0, 0, 0.05);
  width: $width-filter-dropdown;

  @screen lg {
    max-height: 60vh;
  }

  @screen xl {
    width: $width-filter-dropdown-xl;
  }

  @screen 2xl {
    width: $width-filter-dropdown-2xl;
  }
}

.s-datepicker {
  // Custom override for V-Calendar styling
  .vc-popover-content {
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
    z-index: 30;
    top: 6px;
    @include dropdown-clamp;
    @apply px-3 rounded-tl-none rounded-tr-none border-none;
  }

  .vc-container {
    @apply rounded-br-3xl rounded-bl-3xl;
  }

  .vc-popover-caret {
    display: none;
  }
}

.c-search-icon {
  @apply w-3.5 h-3.5 absolute text-black right-0 top-0 mt-1.5 mr-2 shrink-0;

  @screen lg {
    @apply w-4 h-4 text-app-primary-1-red--fire-brick mt-2 mr-6;
  }
}

.c-search-placeholder {
  &::placeholder {
    @apply opacity-100 italic text-black;

    @screen md {
      @apply font-semibold text-xs;
    }

    @screen lg {
      @apply font-semibold text-sm;
    }
  }
}
</style>
